import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {
    USER_ROLE_CLIENT_ADMIN,
    USER_ROLE_CLIENT_NORMAL,
    USER_ROLE_PRODUCTION_ADMIN,
    USER_ROLE_PRODUCTION_NORMAL,
    USER_ROLE_PRODUCTION_OFFSHORE,
    USER_ROLE_SUUMO_ADMIN,
    USER_ROLE_SUUMO_CUSTOMER,
    USER_ROLE_SUUMO_NORMAL,
    USER_TYPE_ADMIN,
    USER_TYPE_NORMAL,
    USER_TYPE_OFFSHORE,
    USER_TYPE_ONETIME,
    UserModel
} from '../models/user.model';
import {HttpUtilsService, QueryParamsModel} from '../_base/crud';
import {environment} from '../../../environments/environment';
import {AuthModel} from '../models/auth.model';
import {NgxFileDropEntry} from 'ngx-file-drop';
import {SPACE_TYPE_SUUMO} from "../models/space.model";
import { ItemsResponse } from '../_base/crud/models/query-models/items-response.model';
import { OKResponse } from '../_base/crud/models/query-models/query-results.model';

const OAUTH_TOKEN_URL = `${environment.pathToAPI}/oauth/token`;
const OAUTH_USER_INFO_URL = `${environment.pathToAPI}/oauth/user_info`;

const API_USERS_URL = `${environment.pathToAPI}/api/v1/users`;

@Injectable()
export class AuthService {
    constructor(private http: HttpClient, private httpUtils: HttpUtilsService) {}

    login(email: string, password: string): Observable<AuthModel> {
        console.log('AuthService.login!');
        const formData: FormData = new FormData();
        formData.append('grant_type', 'password');
        formData.append('username', email);
        formData.append('password', password);
        formData.append('client_id', 'ui');
        formData.append('type', 'studio55');
        return this.http.post<AuthModel>(OAUTH_TOKEN_URL, formData);
    }

    loginSUUMO(email: string, password: string): Observable<AuthModel> {
        console.log('AuthService.loginSUUMO!');
        const formData: FormData = new FormData();
        formData.append('grant_type', 'password');
        formData.append('username', email);
        formData.append('password', password);
        formData.append('client_id', 'ui');
        formData.append('type', 'suumo');
        return this.http.post<AuthModel>(OAUTH_TOKEN_URL, formData);
    }

    getUserByToken(token: string | undefined | null = null): Observable<UserModel> {
        console.log('AuthService.getUserByToken!');
        const httpHeaders = this.httpUtils.getHTTPHeaders(token);
        return this.http.get<UserModel>(OAUTH_USER_INFO_URL, { headers: httpHeaders });
    }

    register(user: UserModel) {
        const httpHeaders = this.httpUtils.getHTTPHeaders();
        return this.http.post<UserModel>(API_USERS_URL, user, { headers: httpHeaders });
    }

    getUserById(userId: number): Observable<UserModel> {
        const httpHeaders = this.httpUtils.getHTTPHeaders();
        return this.http.get<UserModel>(API_USERS_URL + `/${userId}`, {headers: httpHeaders});
    }

    deleteUser(userId: number) {
        const httpHeaders = this.httpUtils.getHTTPHeaders();
        return this.http.delete<OKResponse>(`${API_USERS_URL}/${userId}`, {headers: httpHeaders});
    }

    deleteManyUsers(userIds: number[]) {
        const httpHeaders = this.httpUtils.getHTTPHeaders();
        const url = `${API_USERS_URL}/delete-users`;
        return this.http.put<OKResponse>(url, userIds, { headers: httpHeaders });
    }

    updateUser(user: UserModel,
               files: NgxFileDropEntry[],  // TODO これを消す
               addWatcherIds?: number[],
               removeWatcherIds?: number[],
               addUserGroupIds?: number[],
               removeUserGroupIds?: number[]): Observable<UserModel> {
        console.log('AuthService.updateUser!');
        const httpHeaders = this.httpUtils.getHTTPHeaders();
        const url = `${API_USERS_URL}/${user.id}`;
        return this.http.put<UserModel>(url, {
            ...user,
            addWatcherIds,
            removeWatcherIds,
            addUserGroupIds,
            removeUserGroupIds,
        }, { headers: httpHeaders });
    }

    changePassword(newPassword: string, confirm: boolean = false) {
        const httpHeaders = this.httpUtils.getHTTPHeaders();
        const url = `${API_USERS_URL}/me/password`;
        return this.http.put<{ok: boolean}>(url, {
            password: newPassword,
            confirm,
        }, { headers: httpHeaders });
    }

    makeChangePasswordTrial(email: string) {
        const url = `${API_USERS_URL}/change_password_trial`;
        return this.http.post<{ok: boolean}>(url, {
            email,
        });
    }

    executeChangePasswordTrial(token: string, newPassword: string) {
        const url = `${API_USERS_URL}/change_password_trial/${token}`;
        return this.http.patch<{ok: boolean}>(url, {
            newPassword,
        });
    }

    createUser(user: UserModel, files: NgxFileDropEntry[], addWatcherIds?: number[], addUserGroupIds?: number[]): Observable<UserModel> {
        console.log('AuthService.createUser!');
        const httpHeaders = this.httpUtils.getHTTPHeaders();
        return this.http.post<UserModel>(API_USERS_URL, {
            ...user,
            addWatcherIds,
            addUserGroupIds,
        }, { headers: httpHeaders });
    }

    findUsers(queryParams: QueryParamsModel<UserModel>): Observable<ItemsResponse<UserModel>> {
        const httpHeaders = this.httpUtils.getHTTPHeaders();
        return this.http.post<ItemsResponse<UserModel>>(
            API_USERS_URL + '/find',
            queryParams,
            {headers: httpHeaders }
        );
    }

    sendEmailToAdmin(userId: number) {
        const httpHeaders = this.httpUtils.getHTTPHeaders();
        return this.http.post<OKResponse>(API_USERS_URL + '/send-email-to-admin/' + userId, {},  { headers: httpHeaders });
    }

    resolveRole(user: UserModel) {
        if (user.spaceId) {
            if (user.spaceType === SPACE_TYPE_SUUMO) {
                const favIcon = document.querySelector('#appIcon') as HTMLLinkElement;
                favIcon.href = 'https://suumo.jp/front/img/favicon.ico';
                if (user.userType === USER_TYPE_ONETIME) {
                    user.role = USER_ROLE_SUUMO_CUSTOMER;
                } else if (user.userType === USER_TYPE_ADMIN) {
                    user.role = USER_ROLE_SUUMO_ADMIN;
                } else if (user.userType === USER_TYPE_NORMAL) {
                    user.role = USER_ROLE_SUUMO_NORMAL;
                }
            } else {
                if (user.userType === USER_TYPE_ADMIN) {
                    user.role = USER_ROLE_CLIENT_ADMIN;
                } else if (user.userType === USER_TYPE_NORMAL) {
                    user.role = USER_ROLE_CLIENT_NORMAL;
                }
            }
        } else {
            if (user.userType === USER_TYPE_ADMIN) {
                user.role = USER_ROLE_PRODUCTION_ADMIN;
            } else if (user.userType === USER_TYPE_NORMAL) {
                user.role = USER_ROLE_PRODUCTION_NORMAL;
            } else if (user.userType === USER_TYPE_OFFSHORE) {
                user.role = USER_ROLE_PRODUCTION_OFFSHORE;
            }
        }
    }
}
